import { DataDriver } from "../Drivers/DataDriver.js";
import { LenderDeskDriver } from "../Drivers/LenderDeskDriver.js";

/**
 * For sites that are 100% priced by PmRates' own in-house rate tables (PmRates::
 * DEALER_RATE_CONFIGS with skipLenderDeskFeed=true, full condition coverage) — no real
 * LenderDesk vendor account exists for these dealers at all, unlike the "hybrid" sites
 * (e.g. johnsondodge/gotorcauto, condition-gated PmRates + a real vendor for the rest),
 * which stay on LenderDeskDriver exactly as before and remain PHP-side exceptions in
 * PmRates::isDealerRateEligible() — this class does not change that path at all.
 *
 * Extends LenderDeskDriver rather than duplicating it: PmRatesController's response is
 * deliberately shaped to mirror LenderDeskController's (see its own docblock), and the
 * initial-load cache (LenderDeskPaymentCache, populated by the same batch job either way)
 * is identical either way — so pullApiConfig()/formatApiPayment()/rebate formatting/credit
 * ranges are all inherited unchanged. The only real difference is getPayment(): the parent
 * decides per-request whether to call PmRates or the real vendor
 * (`if (this.initialPayments[type]?.pmRates)`), which only matters for hybrid sites: a
 * fully-PmRates site's cached data always has that flag set anyway, so this override just
 * makes that the *only* path — no runtime branch, no way to ever silently fall through to
 * a real vendor call that doesn't exist for these dealers.
 *
 * isLenderDesk() is deliberately kept returning true — several other files
 * (ContactFormSection.js, VehiclesListApp.js, FrontApp.js, GridWidget.js,
 * PaymentSection.js) check that method to decide real UI behavior (lease defaults, form
 * fields, disclosures) that has nothing to do with which pricing engine is behind the
 * scenes; a fully-PmRates site should look and behave identically to how it does today
 * (as LenderDeskDriver) in all of those, only the actual request/response differs.
 */
export class PmRatesDriver extends LenderDeskDriver {
    constructor(pmdrs) {
        super(pmdrs);
        this.key = DataDriver.PMRATES_KEY;
    }

    isLenderDesk() {
        return true;
    }

    getPayment(taxes, type, paymentData, successCallback, errorCallback) {
        let pmdrs = this.pmdrs;
        let vehicle = pmdrs.frontApp.config.vehicle;
        let requestParams = this.getRequestParams(paymentData, type);

        let sc = (response) => {
            const payments = JSON.parse(response).payments;
            var payment = payments.find((p) => this.offerTypeToPmType(p.offerType) === type) || payments[0];
            if (!payment) {
                successCallback(null);
                return;
            }
            successCallback(this.formatApiPayment(payment));
        };

        let pmRatesParams = {
            Year: vehicle.year,
            SellingPrice: requestParams.PurchasePrice,
            DownPayment: requestParams.DownPayment,
            Vin: vehicle.vin,
            // doc fee doesn't change per recalculation — carry it over from the initial
            // quote so the response keeps surfacing it (SellingPrice already has it baked in)
            DocFee: this.initialPayments[type]?.docFee || 0,
            // PmRates tiers can be condition-gated (e.g. southbay: used vs new APR/term) —
            // without this, dealers with per-condition tiers match nothing and silently
            // fall back to a Cash-only response during recalculation
            Condition: vehicle.condition,
            // nullable — PmRatesController applies every conditional item when omitted,
            // matching a widget build that hasn't shipped this signal yet
            SelectedRebateIds: requestParams.SelectedRebateIds,
        };
        // requestParams.Term is only set for lease/finance (see getRequestParams) — mirror
        // that here instead of always assigning it, otherwise cash recalculation would
        // serialize a literal "Term=undefined" query param
        if (requestParams.Term !== undefined) {
            pmRatesParams.Term = requestParams.Term;
        }

        pmdrs.pmApi.getPmRatesCustomQuotes(pmRatesParams, sc, errorCallback);
    }
}
